import React, { useState, useEffect } from 'react';
import axios from 'axios';
import configApi from '../../config';

const AdminVerification = ({ user }) => {
    const [pendingTerms, setPendingTerms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [feedbackMessage, setFeedbackMessage] = useState(null);

    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    useEffect(() => {
        fetchPendingVerifications();
    }, []);

    const fetchPendingVerifications = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${configApi.apiEndpoint}/api/Terms/PendingVerification`, config);
            setPendingTerms(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching pending verifications:', error);
            setFeedbackMessage({ type: 'danger', text: 'Nepodarilo sa načítať termíny na overenie.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify = async (termId, wasPresent) => {
        try {
            await axios.put(
                `${configApi.apiEndpoint}/api/Terms/${termId}/Verify`,
                wasPresent,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            // Remove the verified item from the queue
            setPendingTerms(prevTerms => prevTerms.filter(t => t.id !== termId));

            setFeedbackMessage({
                type: 'success',
                text: wasPresent ? 'Termín bol označený ako prítomný.' : 'Termín bol označený ako neprítomný.'
            });

            setTimeout(() => setFeedbackMessage(null), 3000);

        } catch (error) {
            console.error('Verification error:', error);
            setFeedbackMessage({ type: 'danger', text: 'Chyba pri overovaní termínu.' });
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString("sk-SK", {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    return (
        <div className="container-fluid py-3">
            <h2 className="mb-4 d-flex align-items-center gap-2">
                <i className="bi bi-clipboard-check text-primary"></i>
                Overenie prítomnosti
            </h2>

            {feedbackMessage && (
                <div className={`alert alert-${feedbackMessage.type} mb-4`} role="alert">
                    {feedbackMessage.text}
                </div>
            )}

            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Načítavam...</span>
                    </div>
                </div>
            ) : pendingTerms.length === 0 ? (
                <div className="alert alert-success d-flex align-items-center gap-3 py-4">
                    <i className="bi bi-check-circle-fill fs-2"></i>
                    <div>
                        <h5 className="mb-1">Všetko je aktuálne!</h5>
                        <p className="mb-0">Nemáte žiadne minulé termíny, ktoré by čakali na overenie prítomnosti.</p>
                    </div>
                </div>
            ) : (
                <div className="row g-3">
                    {pendingTerms.map(term => (
                        <div key={term.id} className="col-12 col-lg-6 col-xl-4">
                            <div className="card shadow-sm h-100 border-0 rounded-4 overflow-hidden">
                                <div className="card-body p-3 d-flex flex-column">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-primary bg-opacity-10 text-primary rounded px-2 py-1 text-center font-monospace fw-bold" style={{ minWidth: '45px' }}>
                                                {new Date(term.date).toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit" })}
                                            </div>
                                            <span className="text-muted small fw-medium">
                                                {new Date(term.date).toLocaleDateString("sk-SK", { day: "2-digit", month: "long" })}
                                            </span>
                                        </div>
                                        <span className="badge bg-secondary bg-opacity-10 text-dark border rounded-pill">
                                            <i className="bi bi-hourglass-split me-1"></i>{term.duration} min
                                        </span>
                                    </div>

                                    <div className="mb-3 flex-grow-1">
                                        <h5 className="mb-2 fw-bold text-dark text-truncate" title={term.termType?.name || 'Vlastný termín'}>
                                            <i className="bi bi-bookmark-fill text-primary me-2"></i>
                                            {term.termType?.name || 'Vlastný termín'}
                                        </h5>

                                        <div className="d-flex align-items-center text-muted small mb-2 text-truncate">
                                            <i className="bi bi-person-fill me-2 fs-6"></i>
                                            {term.patient ? (
                                                <span className="fw-medium text-dark">{`${term.patient.firstName} ${term.patient.lastName}`}</span>
                                            ) : (
                                                <span>Pacient nepriradený</span>
                                            )}
                                        </div>

                                        {term.userNotes && (
                                            <div className="small bg-warning bg-opacity-10 text-dark rounded px-2 py-1 mt-2 text-truncate border border-warning border-opacity-25" title={term.userNotes}>
                                                <i className="bi bi-chat-text me-1 text-warning"></i> {term.userNotes}
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex gap-2 mt-auto">
                                        <button
                                            onClick={() => handleVerify(term.id, true)}
                                            className="btn btn-success flex-grow-1 fw-bold py-2 shadow-sm"
                                        >
                                            <i className="bi bi-check-lg me-1"></i> Prítomný
                                        </button>
                                        <button
                                            onClick={() => handleVerify(term.id, false)}
                                            className="btn btn-danger flex-grow-1 fw-bold py-2 shadow-sm"
                                        >
                                            <i className="bi bi-x-lg me-1"></i> Neprišiel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )
            }
        </div >
    );
};

export default AdminVerification;
